import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiService } from 'services/base.service';
import { ReactComponent as DotIcon } from 'assets/icons/dot-icon.svg';
import RequestItemMenu from 'pages/main-store/item-requests/item-request-details/request-item-menu';
import { RequestType } from 'models/item-request.model';
import { ENUM_REQUEST_STATUS } from 'models/common.model';
import RequestStatusTag from 'components/base/request-status-tag';
import RequestDetailsWrapper from 'components/base/request-details-wrapper';
import useRoleContext from 'contexts/role.context';
import { InventoryModule, RolePermission } from 'models/permission.model';

const UpdateKitchenProcurement = () => {
  const [loading, setLoading] = useState(false);
  const [internalProcurement, setInternalProcurement] = useState({} as RequestType);
  const { id } = useParams();
  const { can } = useRoleContext();
  const canWriteProcurement = can(InventoryModule.KitchenRequisition, RolePermission.Write);

  const getRequestDetails = async () => {
    setLoading(true);
    try {
      const res: any = await apiService.get(`kitchen-store/requests/${id}`, { version: 'v2' });
      setInternalProcurement(res.data.data);
    } catch (e: any) {
      console.log(e, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getRequestDetails();
  }, []);

  const is_completed = internalProcurement.status === ENUM_REQUEST_STATUS.COMPLETED;
  const header = is_completed
    ? 'Request Completed'
    : internalProcurement.status === ENUM_REQUEST_STATUS.RECEIVED
    ? 'Request received'
    : 'Update request';

  return (
    <RequestDetailsWrapper
      codeId={internalProcurement?.code}
      status={internalProcurement?.status}
      header={header}
      subHeader={
        !is_completed ? 'Update item as received or returned once vendor delivers' : undefined
      }>
      {loading ? (
        <div className='flex justify-center items-center w-full h-full'>
          <p className=''>Loading...</p>
        </div>
      ) : internalProcurement?.items?.length ? (
        internalProcurement.items.map((item, index) => {
          const localStatus = item.status?.toUpperCase() as ENUM_REQUEST_STATUS;
          return (
            <div key={index} className='mb-4'>
              <div
                className={`flex justify-between py-[18px] px-[30px] border-b border-b-[#F7F7F8] ${
                  localStatus === ENUM_REQUEST_STATUS.PENDING && 'opacity-50'
                }`}
                key={index}>
                <div className='flex gap-4 items-center'>
                  <div>
                    <p className='font-medium text-[#2E3031] capitalize'>{item.ingredient.name}</p>
                    <div className='flex gap-2 flex-wrap items-center'>
                      <p className='text-sm font-medium text-[#6F7376]'>
                        {item.quantity} {item.ingredient.unit_type} requested
                      </p>
                    </div>
                  </div>
                </div>
                <div className='flex flex-col items-end  gap-6'>
                  <RequestStatusTag status={item.status?.toUpperCase() as ENUM_REQUEST_STATUS} />

                  {canWriteProcurement && [ENUM_REQUEST_STATUS.SENT].includes(localStatus) && (
                    <RequestItemMenu
                      status={localStatus}
                      ids={item.id}
                      onSuccess={getRequestDetails}
                    />
                  )}
                </div>
              </div>
              <div>
                {item.batches.map((batch, batchIndex) => (
                  <div
                    key={batchIndex}
                    className='flex items-center gap-2 text-sm py-1 font-medium px-8 '>
                    <p className='text-[#6F7376] min-w-[60px]'>Batch {batch.batch_no}</p>
                    <DotIcon className='text-[#D9D9D9]' />
                    <p className='text-[#33A5FF]'>
                      {batch.quantity} {item.ingredient.unit_type || 'gram'} sent
                    </p>
                  </div>
                ))}
                <div className='flex items-center gap-2 text-sm font-medium mx-8 mt-4 '>
                  <p className='min-w-[50px]'>Total</p>
                  <DotIcon className='text-[#D9D9D9]' />

                  <p className='text-[#33A5FF]'>
                    {item.batches.reduce((acc, batch) => acc + Number(batch.quantity), 0)}{' '}
                    {item.ingredient.unit_type || ' gram'} sent
                  </p>
                </div>
              </div>
            </div>
          );
        })
      ) : null}
    </RequestDetailsWrapper>
  );
};

export default UpdateKitchenProcurement;
